import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@gsa/afp-component-library';
import { useContractLine } from '../provider/contract-line-provider';
import {
  getOptionalEcList,
  ecTitleLookup,
  hasConflicts,
} from '../helpers/ec-helpers';

export const getEditConflictBlock = (
  equipment,
  field,
  getFormFn,
  getSummaryFn,
) => {
  return (
    <div className="grid-row bg-primary-lightest padding-2">
      <div className="grid-col-7">
        <div>
          <div className="text-bold text-primary text-uppercase">{field}</div>
          <div>Equipment code(s)</div>
        </div>
        {getFormFn()}
      </div>

      <div className="grid-col-5">
        <div className="text-bold text-primary text-uppercase">
          {field} summary
        </div>
        <div className="margin-bottom-1">
          {equipment} <span className="text-bold text-uppercase">{field}</span>
        </div>
        {getSummaryFn()}
      </div>
    </div>
  );
};

export const displayCludes = (ecs, ecList) => {
  return (
    <ul className="margin-y-0">
      {ecs.map((ec) => {
        const ecTitle = ecTitleLookup(ec, ecList);
        const isArchived = ecTitle.endsWith('[ARCHIVED]');
        return (
          <li key={ec}>
            <span className={isArchived ? 'text-italic' : ''}>{ecTitle}</span>
          </li>
        );
      })}
    </ul>
  );
};

export const displayRequires = (relations, ecList) => {
  return relations.map((row, i) => (
    <div key={`requires-${row.join('')}`}>
      <ul className="margin-y-0">
        {row.map((ec, j) => {
          const ecTitle = ecTitleLookup(ec, ecList);
          const isArchived = ecTitle.endsWith('[ARCHIVED]');
          return (
            <li key={ec}>
              <span className={isArchived ? 'text-italic' : ''}>{ecTitle}</span>
              {j < row.length - 1 && ', '}
              {j < row.length - 1 && (
                <span className="text-bold font-sans-3xs">AND</span>
              )}
            </li>
          );
        })}
      </ul>
      {i < relations.length - 1 && (
        <div className="text-bold font-sans-2xs">OR</div>
      )}
    </div>
  ));
};

export const ConflictsDisplayBlock = ({
  row = {},
  original = {},
  onActionClick,
  isReadOnly = false,
  isStandaloneEC = false,
}) => {
  const { optionalECs } = useContractLine();

  // hide conflicts for Standard optional equipment
  if (!isStandaloneEC) return null;

  const ecList = getOptionalEcList(optionalECs);
  const noConflict = !hasConflicts(original);

  const showConflict = (header, field, displayFn) => {
    if (!original[field]?.newValue.length) return null;
    return (
      <div className="grid-row margin-bottom-2">
        <div className="grid-col-2 text-bold text-right padding-right-1">
          {header}
        </div>
        <div className="grid-col-10">
          {displayFn(original[field].newValue, ecList)}
        </div>
      </div>
    );
  };

  return (
    <div>
      {original.status?.newValue === 'Off' && (
        <div className="grid-row margin-y-05">
          <div className="display-block bg-base-lighter padding-x-1">
            ARCHIVED
          </div>
        </div>
      )}
      <div className="grid-row">
        <div className="grid-col-2 text-bold text-primary padding-y-05">
          CONFLICTS
        </div>
        {!isReadOnly && (
          <div className="grid-col-10 padding-left-3 padding-y-05">
            <Button
              variant="unstyled"
              label={`${noConflict ? 'Add' : 'Edit'} conflict`}
              leftIcon={{ name: noConflict ? 'add' : 'edit' }}
              onClick={() => onActionClick('conflict', row)}
            />
          </div>
        )}
      </div>
      <div className="margin-top-1 margin-bottom-2">
        {showConflict('Includes', 'includes', displayCludes)}
        {showConflict('Excludes', 'excludes', displayCludes)}
        {showConflict('Requires', 'requires', displayRequires)}
      </div>
    </div>
  );
};

ConflictsDisplayBlock.propTypes = {
  row: PropTypes.shape(Object),
  original: PropTypes.shape(Object),
  onActionClick: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  isStandaloneEC: PropTypes.bool,
};
